import { z } from 'zod';
import type { AccountType } from '../constants/accountTypes.js';

const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'expected YYYY-MM-DD');

// ── Query schemas ─────────────────────────────────────────────────────────────

export const asOfQuerySchema = z.object({
  asOf: isoDate.optional(),
});
export type AsOfQuery = z.infer<typeof asOfQuerySchema>;

export const periodQuerySchema = z.object({
  from: isoDate,
  to: isoDate,
});
export type PeriodQuery = z.infer<typeof periodQuerySchema>;

export const generalLedgerQuerySchema = z.object({
  from: isoDate.optional(),
  to: isoDate.optional(),
  accountId: z.string().uuid().optional(),
});
export type GeneralLedgerQuery = z.infer<typeof generalLedgerQuerySchema>;

/** Drill-down: the transactions behind a report figure (one or more account codes). */
export const drilldownQuerySchema = z.object({
  codes: z
    .string()
    .min(1)
    .transform((s) => s.split(',').map((x) => x.trim()).filter(Boolean))
    .pipe(z.array(z.string()).min(1)),
  from: isoDate.optional(),
  to: isoDate.optional(),
});
export type DrilldownQuery = z.infer<typeof drilldownQuerySchema>;

export interface DrilldownLine {
  date: string;
  entryNumber: string;
  journalEntryId: string;
  companyId: string;
  companyName: string | null; // populated only for consolidated drill-downs
  accountCode: string;
  accountName: string;
  /** What created the entry: manual | invoice | bank_match | import | ... */
  source: string;
  memo: string | null;
  debit: string;
  credit: string;
  /**
   * Aggregate running balance across the drilled codes immediately after this
   * line posted, signed on the codes' normal side. Null when the codes span
   * mixed normal-balance sides (e.g. a custom multi-account figure). If a
   * `from` filter was supplied, this INCLUDES prior-period activity so the
   * running balance reflects the true account balance, not just the period.
   */
  balanceAfter: string | null;
}

export interface DrilldownResponse {
  codes: string[];
  from: string | null;
  to: string | null;
  lines: DrilldownLine[];
  totals: { debit: string; credit: string };
  /**
   * Opening balance across the drilled codes as of `from` (or 0 if no from
   * filter). Signed on the codes' normal side. Null when codes span mixed
   * normal-balance sides.
   */
  openingBalance: string | null;
}

// ── Export (XLSX / PDF) ───────────────────────────────────────────────────────

export const REPORT_EXPORT_TYPES = [
  'balance_sheet',
  'income_statement',
  'trial_balance',
  'general_ledger',
  'cash_flow',
  'weekly_profitability',
] as const;
export type ReportExportType = (typeof REPORT_EXPORT_TYPES)[number];

export const exportReportQuerySchema = z.object({
  type: z.enum(REPORT_EXPORT_TYPES),
  format: z.enum(['xlsx', 'pdf']),
  asOf: isoDate.optional(),
  from: isoDate.optional(),
  to: isoDate.optional(),
  accountId: z.string().uuid().optional(),
  // Query params arrive as strings; treat only the literal "true" as on so the
  // exported report matches the on-screen "Active Only" toggle.
  activeOnly: z.preprocess((v) => v === 'true' || v === true, z.boolean()).optional(),
});
export type ExportReportQuery = z.infer<typeof exportReportQuerySchema>;

// ── Shared response shapes ────────────────────────────────────────────────────

export interface ReportLine {
  accountId: string;
  code: string;
  name: string;
  /** Balance on the account's normal side, as a money string. */
  amount: string;
  /** Indentation level for subaccount nesting (0 = top-level). */
  depth?: number;
  /** True for a synthetic "Total <parent>" subtotal row (not drillable). */
  isSubtotal?: boolean;
  /**
   * True when there's at least one non-void journal line posted to this account
   * (or its descendants for parent rows) in the period. Used by the frontend to
   * render a row with zero amount as blank (no activity) vs "$0.00" (transactions
   * netted to zero — worth a look). Defaults to false when omitted by older
   * report responses.
   */
  hasActivity?: boolean;
  /**
   * All account codes this line covers. Single-company lines omit it (use `code`).
   * Consolidated lines group accounts by name+type across companies, so they carry
   * every contributing code here for accurate drill-down.
   */
  codes?: string[];
}

export interface ReportSection {
  /** Account subtype key (e.g. 'bank') or a synthetic key. */
  key: string;
  label: string;
  lines: ReportLine[];
  total: string;
}

// ── Balance Sheet ─────────────────────────────────────────────────────────────

export interface BalanceSheetGroup {
  total: string;
  sections: ReportSection[];
}

export interface BalanceSheetResponse {
  asOf: string;
  fiscalYearStart: string;
  assets: BalanceSheetGroup;
  liabilities: BalanceSheetGroup;
  equity: BalanceSheetGroup; // includes synthetic "Net Income (current period)" line
  netIncome: string;
  totals: { assets: string; liabilities: string; equity: string };
  balanced: boolean;
}

// ── Income Statement ──────────────────────────────────────────────────────────

export interface IncomeStatementResponse {
  from: string;
  to: string;
  revenue: ReportSection;
  cogs: ReportSection;
  grossProfit: string;
  expenses: ReportSection;
  operatingIncome: string;
  otherIncome: ReportSection;
  otherExpense: ReportSection;
  netIncome: string;
}

// ── Trial Balance ─────────────────────────────────────────────────────────────

export interface TrialBalanceRow {
  accountId: string;
  code: string;
  name: string;
  accountType: AccountType;
  debit: string;
  credit: string;
}

export interface TrialBalanceResponse {
  asOf: string;
  rows: TrialBalanceRow[];
  totals: { debit: string; credit: string };
  balanced: boolean;
}

// ── General Ledger ────────────────────────────────────────────────────────────

export interface GeneralLedgerLine {
  date: string;
  entryNumber: string;
  journalEntryId: string;
  /** What created the entry: manual | invoice | bank_match | import | ... */
  source: string;
  memo: string | null;
  debit: string;
  credit: string;
  /** Running balance on the account's normal side after this line. */
  balance: string;
  /**
   * True if the JE this line belongs to is locked. Locked entries are either
   * (a) closed by a formal reconciliation session, or (b) manually marked
   * reconciled by the user for historical/imported data. The matcher uses
   * this to avoid suggesting locked entries as candidates for new bank txns.
   */
  isLocked: boolean;
  /**
   * Reconciliation that locked this entry, if any. Null when the entry was
   * locked manually (e.g. bulk-marked from historical imports).
   */
  lockedByReconciliationId: string | null;
  /**
   * True when the JE is matched to a bank-feed transaction (bank_transactions
   * row with matched_journal_entry_id = this.id and match_status in
   * 'matched' or 'reconciled'). A reconciled entry is always cleared too.
   */
  isCleared: boolean;
}

export interface GeneralLedgerAccount {
  accountId: string;
  code: string;
  name: string;
  accountType: AccountType;
  openingBalance: string;
  lines: GeneralLedgerLine[];
  endingBalance: string;
}

export interface GeneralLedgerResponse {
  from: string | null;
  to: string | null;
  accounts: GeneralLedgerAccount[];
}

// ── Cash Flow (indirect) ──────────────────────────────────────────────────────

export interface CashFlowLine {
  label: string;
  amount: string;
  /** Account code(s) behind this line, for drill-down. */
  codes: string[];
}

export interface CashFlowSection {
  label: string;
  lines: CashFlowLine[];
  total: string;
}

export interface CashFlowResponse {
  from: string;
  to: string;
  netIncome: string;
  operating: CashFlowSection;
  investing: CashFlowSection;
  financing: CashFlowSection;
  netChangeInCash: string;
  beginningCash: string;
  endingCash: string;
  /** Cash/bank account codes — for drilling the cash balance rows. */
  cashCodes: string[];
  /** True when netChange + beginningCash === endingCash (always should be). */
  reconciled: boolean;
}

// ── Weekly profitability ──────────────────────────────────────────────────────

/**
 * One calendar week of the P&L. Weeks are fixed 7-day blocks counted from
 * Jan 1 — Week 1 is Jan 1–7, Week 2 is Jan 8–14 — so a week number means the
 * same dates every year. Week 53 is the 1–2 days left over at year end.
 */
export interface WeeklyProfitabilityRow {
  year: number;
  /** 1–53, numbered within `year`. Survives any quarter/month filter. */
  weekNumber: number;
  /** The days of this week that fall inside the requested period. */
  from: string;
  to: string;
  /** True when the period boundary (or year end) cut this week short of 7 days. */
  partial: boolean;
  revenue: string;
  cogs: string;
  grossProfit: string;
  expenses: string;
  /** Other income less other expense. */
  otherNet: string;
  netIncome: string;
}

export interface WeeklyProfitabilityResponse {
  from: string;
  to: string;
  weeks: WeeklyProfitabilityRow[];
  totals: Pick<
    WeeklyProfitabilityRow,
    'revenue' | 'cogs' | 'grossProfit' | 'expenses' | 'otherNet' | 'netIncome'
  >;
  /** Account codes with activity in the period, per column — for drilling a cell. */
  codes: { revenue: string[]; cogs: string[]; expenses: string[]; other: string[] };
}
